<template>
  <div class="search-map-content" v-on-clickaway="closeSearch">
    <v-btn
      class="search-map-icon"
      icon
      v-if="!searchIsOpen"
      @click="searchIsOpen = true"
    >
      <v-icon color="white"> search </v-icon>
    </v-btn>

    <v-text-field
      v-else
      v-model="search"
      @input="searchObjectsByLabel"
      rounded
      solo-inverted
      clearable
      color="white"
      class="search-map-text-field ma-0"
      id="search-map-text-field"
      prepend-inner-icon="search"
    >
      <template v-slot:append>
        <v-fade-transition leave-absolute>
          <v-progress-circular
            v-if="loading"
            size="20"
            color="info"
            indeterminate
            id="search-progress-circular"
          />
        </v-fade-transition>
      </template>
    </v-text-field>
    <v-layout
      v-if="noResults && search && objectsFound"
      class="objects-found-card pt-7 pb-2"
    >
      <v-alert class="objects-found-text" icon="warning">
        {{ $t("tables.SearchMessageInitial") }} "{{ search }}"
        {{ $t("tables.SearchMessageEnd") }}
      </v-alert>
    </v-layout>
    <v-layout
      column
      v-show="search && objectsFound && objectsFound.length > 0 && !noResults"
      class="objects-found-card pt-7 pb-2"
    >
      <div
        v-for="layer in objectsFound"
        :key="layer.layer"
        v-if="layer.objects.length > 0"
        class="layer-object-content  my-0"
      >
        <div
          v-for="(object, index) in layer.objects"
          :key="index"
          @click="centerObjectOnMap(object, layer)"
          class="objects-found pl-6"
        >
          <span class="ml-3 objects-found-text objects-found-text-title ">
            {{
              getLayerTranslationKey(layer.layer) +
                ", " +
                $t("ID") +
                getObjectField(object, layer.prefix, "id")
            }}
          </span>

          <div
            v-if="getObjectField(object, layer.prefix, 'label')"
            class="ml-3"
          >
            <span class="objects-found-text objects-found-text-subtitle">
              {{ $tc("Label", 1) }}:
              {{ getObjectField(object, layer.prefix, "label") }}
            </span>
          </div>
        </div>
      </div>
    </v-layout>
  </div>
</template>

<script>
import { mixin as clickaway } from "vue-clickaway";
import { mapActions } from "vuex";
import { layersMixin } from "@/mixins/layersMixin";
export default {
  name: "SearchMap",
  mixins: [clickaway, layersMixin],

  data() {
    return {
      /**
       * Determines if search has been selected
       */
      searchIsOpen: false,

      /**
       * Save the search value
       */
      search: "",

      /**
       * Objects returned by the BE corresponding to the search value
       */
      objectsFound: null,

      loading: false,

      /**
       * Determines if the survey had any value
       */
      noResults: false
    };
  },

  watch: {
    /**
     * If the variable is changed, it will check if there was any object found,
     * to set the value of the variable noResults
     */
    objectsFound() {
      this.noResults = true;
      if(this.objectsFound){
      for (let layer of this.objectsFound) {
        if (layer && layer.objects.length > 0) {
          this.noResults = false;
          return;
        }
      }
    }
    }
  },

  methods: {
    ...mapActions(["SEARCH_OBJECTS_BY_ID", "SET_OBJECT_TO_ZOOM_ON_MAP"]),

    /**
     * Close the search window
     */
    closeSearch() {
      this.searchIsOpen = false;
      this.objectsFound = null;
      this.search = "";
    },

    /**
     * Makes a request to the BE to return the objects where the label matches the search value
     * @returns {Promise<void>}
     */
    async searchObjectsByLabel() {
      this.objectsFound = null;
      if (this.search === "" || !this.search) {
        return;
      }
      try {
        this.loading = true;
        let activeLayer = this.$store.getters.getNetworkActiveLayers;
        let activeLayerNames = this.getActiveLayersNamesList(activeLayer);

        let typeObjectsFound = await this.SEARCH_OBJECTS_BY_ID({
          searchFor: "label",
          word: this.search,
          layers: { layers: activeLayerNames }
        });
        this.objectsFound = typeObjectsFound.data.data;
      } catch (error) {
        this.objectsFound = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Get object field value
     * @param object
     * @param prefix - layer prefix (example: bd_ (buildings)
     * @param field
     * @returns {*}
     */
    getObjectField(object, prefix, field) {
      return object[prefix + field];
    },

    /**
     * centers object on map (internal API parameters)
     */
    centerObjectOnMap(object) {
      let objectGeoJson = this.searchObjectField(object, "geo_json");

      this.$nextTick(() => {
        this.SET_OBJECT_TO_ZOOM_ON_MAP(undefined).then(() => {
          this.SET_OBJECT_TO_ZOOM_ON_MAP(objectGeoJson);
          this.closeSearch();
        });
      });
    },

    /**
     * Scroll the object to find the geom
     * @param object
     * @param field
     * @returns {{"type":"Point","coordinates":[-8.66135716438293,41.1562375075913]}}
     */
    searchObjectField(object, field) {
      for (const [key, value] of Object.entries(object)) {
        if (typeof value === "object" && value !== null) {
          let fieldFounded = this.searchObjectField(value, field);
          if (fieldFounded) return fieldFounded;
        }
        if (key.includes(field) && value) {
          return value;
        }
      }
    },

    /**
     * Get's the name to exhibit on the layer option label.
     * @param {String} layer - layer being analysed
     * @returns {String}
     */
    getLayerTranslationKey(layer) {
      return this.$store.getters.getLayerTranslationKey(layer);
    }
  }
};
</script>

<style scoped>
.search-map-content {
  text-align: initial;
  width: 300px;
  z-index: 3;
  position: absolute;
  left: 1%;
  top: 1%;
}

.search-map-icon {
  background: #32383d 0 0 no-repeat padding-box;
  border-radius: 38px;
  opacity: 1;
  height: 40px !important;
  width: 40px !important;
}

.search-map-text-field {
  z-index: 3;
  position: absolute;
  width: 100%;
}

.v-menu__content {
  top: 48px !important;
}

.objects-found {
  line-height: normal;
}

.objects-found:hover {
  background-color: rgba(236, 236, 236, 0.747);
}

.objects-found-text {
  text-align: left;
  font: normal normal normal 12px/14px Segoe UI;
}

.objects-found-text-title {
  color: #32383d;
}

.objects-found-text-subtitle {
  font: normal normal normal 10px/14px Segoe UI;
  color: #b1b6b9;
}

.objects-found-card {
  background-color: white;
  z-index: 2;
  top: 22px;
  position: relative;
  max-height: 300px;
  overflow-y: auto;
}
</style>

<style>
#search-progress-circular .v-progress-circular__overlay {
  stroke-width: 6px !important;
}
.search-map-text-field.v-text-field--solo-inverted
  > .v-input__control
  > .v-input__slot {
  background: #32383d 0 0 no-repeat padding-box !important;
  min-height: unset;
  height: 40px;
}

.search-map-text-field.theme--light.v-input input,
.search-map-text-field.theme--light.v-input input:after,
.search-map-text-field.v-icon.v-icon,
.search-map-text-field.theme--light.v-icon {
  color: white !important;
  font: normal normal 600 14px/19px Segoe UI;
}

.search-map-text-field.v-text-field--rounded
  > .v-input__control
  > .v-input__slot {
  padding: 0 10px !important;
}

.search-map-text-field .v-icon {
  font-size: 24px !important;
  color: white !important;
}

.search-map-text-field
  .v-text-field.v-text-field--enclosed
  .v-text-field__details {
  height: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Network/Map/SearchMap.vue